// @flow
import React from "react";
import { get, noop } from "lodash";
import type { UnaryFn } from "@tvg/types/Functional";
import formatCurrency from "@tvg/formatter/currency";
import buildRaceUrl from "@tvg/formatter/url";
import Icon from "@fdr/static-ui/Icons";
import { arrowRight } from "@fdr/static-ui/Icons/icons";
import buildColor from "@fdr/static-ui/ColorPalette";
import type { TopPool, TopPoolRace } from "@tvg/types/Pool";
import {
  PanelContainer,
  InformationWrapper,
  MtpWrapper,
  Title,
  Description,
  RaceWrapper,
  TrackNameWrapper,
  RaceNumberWrapper,
  Value,
  Pick,
  FooterWrapper
} from "./styled-components";
import MTP, { getMTPStatus } from "../../_atoms/MTP";
import { getMTPColor } from "../../_atoms/MTP/styled-components";

type Props = {
  onPanelClick: UnaryFn<TopPool, void>,
  pool: TopPool
};

const isFeaturedRace = (poolHighlight: string) =>
  poolHighlight === "Highlighted";

const renderFeaturedRaceWrapper = (trackName: string, raceNumber: string) => (
  <RaceWrapper data-qa-label="poolRaceDescription">
    <TrackNameWrapper>{trackName}&nbsp;</TrackNameWrapper>
    <RaceNumberWrapper>R{raceNumber}</RaceNumberWrapper>
  </RaceWrapper>
);

const renderPoolInformation = ({
  track,
  amount,
  wagerType,
  race: { highlighted, raceNumber }
}: TopPool) => {
  const isFeatured = isFeaturedRace(get(highlighted, "style"));

  return (
    <InformationWrapper>
      <Title data-qa-label="PoolTitle">
        {isFeatured ? get(highlighted, "description") : track}
      </Title>
      <Description data-qa-label="PoolDescription">
        {isFeatured
          ? renderFeaturedRaceWrapper(track, raceNumber)
          : `Race ${raceNumber}`}
      </Description>
      <Value data-qa-label="PoolValue">
        {formatCurrency(amount, "USD", 0)}
      </Value>
      <Pick data-qa-label="PoolPick">{get(wagerType, "name")}</Pick>
    </InformationWrapper>
  );
};

const renderFooter = (isFeatured: boolean, race: TopPoolRace) => {
  const mtp = get(race, "mtp");
  const mtpMode = isFeatured ? "dark" : "light";
  return (
    <FooterWrapper
      data-qa-label="poolBetNowText"
      $isFeatured={isFeatured}
      isNewLayout
    >
      <MTP
        isPlainMTP
        mtp={mtp}
        postTime={get(race, "postTime")}
        isSmall
        isNewLayout
        mode={mtpMode}
      />
      <Icon
        icon={arrowRight}
        color={getMTPColor(
          getMTPStatus(get(race, "status.code"), mtp),
          mtpMode
        )}
        size={17}
        qaLabel="topPoolArrowIcon"
      />
    </FooterWrapper>
  );
};

const TopPoolPanel = ({ onPanelClick, pool }: Props) => {
  const url = `${buildRaceUrl(
    get(pool, "trackCode"),
    get(pool, "track"),
    get(pool, "race.raceNumber")
  )}&betType=${get(pool, "wagerType.id")}`;

  const isFeatured = isFeaturedRace(get(pool, "race.highlighted.style"));

  return (
    <PanelContainer
      to={url}
      data-qa-label="pool-link"
      onClick={() => onPanelClick(pool)}
      $isFeatured={isFeatured}
    >
      {renderPoolInformation(pool)}
      {renderFooter(isFeatured, get(pool, "race"))}
    </PanelContainer>
  );
};

TopPoolPanel.defaultProps = {
  onPanelClick: noop,
  pool: {
    track: "",
    trackCode: "",
    amount: 0,
    wagerType: {
      id: 0,
      name: "",
      code: ""
    },
    race: {
      raceNumber: "",
      postTime: "",
      mtp: 0,
      isGreyhound: false,
      highlighted: {
        description: "",
        pinnedOrder: 0,
        style: ""
      }
    },
    poolType: ""
  }
};

export default TopPoolPanel;
